// /////////responsive
import React, { useContext, useEffect, useState } from "react";
import {
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  IconButton,
  Toolbar,
  useMediaQuery,
  useTheme,
  Box,
  Tooltip,
  Typography,
  Avatar,
} from "@mui/material";
import {
  Home,
  ShoppingCart,
  Restaurant,
  People,
  Receipt,
  BarChart,
  PieChart,
  Settings,
  AccountCircle,
  ChevronRight,
  Menu,
  KeyboardArrowLeft,
} from "@mui/icons-material";
import { Link, useLocation } from "react-router-dom";
import { ThemeContext } from "../../context/ThemeContextProvider";
import { rolePages } from "../../roles";
import { getActiveDailySessionData } from "../../services/Settings/ServicesandTaxSettingsAndSessios/SessoinAndShiftsApi";

const Sidebar = ({ isOpen, toggleSidebar }) => {
  const { mode } = useContext(ThemeContext);
  const location = useLocation();
  const theme = useTheme();
  const [sessionDate, setSessionDate] = useState(null);

  // ✅ كشف دقيق لجميع أحجام التابلت
  const isSmallTablet = useMediaQuery("(min-width: 600px) and (max-width: 768px)");
  const isMediumTablet = useMediaQuery("(min-width: 769px) and (max-width: 900px)");
  const isLargeTablet = useMediaQuery("(min-width: 901px) and (max-width: 1024px)");
  const isTablet = useMediaQuery("(min-width: 600px) and (max-width: 1024px)");
  
  // ✅ كشف الوضع الأفقي/العمودي
  const isPortrait = useMediaQuery("(orientation: portrait)");
  const isLandscape = useMediaQuery("(orientation: landscape)");

  // ✅ حساب عرض Sidebar حسب حجم الشاشة والوضع
  const getSidebarWidth = () => {
    if (!isOpen) {
      return isSmallTablet ? 70 : 80;
    }
    
    if (isSmallTablet) {
      return isPortrait ? 240 : 200;
    } else if (isMediumTablet) {
      return isPortrait ? 260 : 220;
    } else if (isLargeTablet) {
      return isPortrait ? 280 : 240;
    }
    return 280;
  };

  const sidebarWidth = getSidebarWidth();

  // ✅ جلب تاريخ اليومية الحالية
  useEffect(() => {
    const fetchSessionDate = async () => {
      try {
        const branchId = localStorage.getItem("branch_id");

        if (!branchId) {
          console.warn("⚠️ لا يوجد branch_id في localStorage");
          return;
        }

        const resp = await getActiveDailySessionData(branchId);

        // ✅ التحقق من الاستجابة - الكائن يأتي مباشرة أو داخل data
        if (resp && resp.session_date) {
          setSessionDate(resp.session_date);
        } else if (resp && resp.data && resp.data.session_date) {
          setSessionDate(resp.data.session_date);
        }
      } catch (err) {
        console.error("❌ فشل في جلب تاريخ اليومية:", err);
      }
    };

    if (isTablet) {
      fetchSessionDate();
    }
  }, [isTablet]);

  // ✅ إذا لم يكن تابلت، لا تعرض Sidebar
  if (!isTablet) {
    return null;
  }

  // ✅ جلب بيانات المستخدم
  const user = JSON.parse(localStorage.getItem("user"));
  const role = user?.type;
  const allowedPages = rolePages[role] || [];

  // ✅ قائمة الصفحات المتاحة
  const menuItems = [
    { text: "لوحة التحكم", icon: <Home />, path: "/dashboard" },
    { text: "شاشة البيع", icon: <ShoppingCart />, path: "/pos" },
    { text: "إدارة القائمة", icon: <Restaurant />, path: "/menu" },
    { text: "الحسابات", icon: <ShoppingCart />, path: "/accounts" },
    { text: "إدارة العملاء", icon: <People />, path: "/customers" },
    { text: "إدارة الفواتير", icon: <Receipt />, path: "/invoices" },
    { text: "التقارير المالية", icon: <BarChart />, path: "/reports" },
    { text: "الإحصائيات", icon: <PieChart />, path: "/Statics" },
    { text: "إدارة الموظفين", icon: <People />, path: "/staff" },
    { text: "إدارة المستخدمين", icon: <AccountCircle />, path: "/users" },
    { text: "الإعدادات", icon: <Settings />, path: "/settings" },
  ];

  // ✅ حساب أحجام النصوص والأيقونات حسب حجم الشاشة
  const getIconSize = () => {
    if (isSmallTablet) return isOpen ? 20 : 22;
    if (isMediumTablet) return isOpen ? 22 : 24;
    return isOpen ? 24 : 26;
  };

  const getTextSize = () => {
    if (isSmallTablet) return "13px";
    if (isMediumTablet) return "14px";
    return "15px";
  };

  return (
    <Drawer
      variant="permanent"
      anchor="right"
      open={isOpen}
      ModalProps={{
        keepMounted: true,
        container: document.body,
        style: { zIndex: 1100 },
      }}
      sx={{
        "& .MuiDrawer-paper": {
          width: sidebarWidth,
          transition: "width 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          overflowY: "auto",
          overflowX: "hidden",
          direction: "rtl",
          backgroundColor: mode === "dark" ? "#1F1D2B" : "#fafafa",
          color: mode === "dark" ? "#fff" : "#000",
          height: "100vh",
          position: "fixed",
          top: 0,
          right: 0,
          borderLeft: `3px solid ${mode === "dark" ? "#EA7C69" : "#ff6b6b"}`,
          boxShadow: "4px 0 20px rgba(0,0,0,0.12)",
          zIndex: 1100,
          // ✅ دعم safe area
          paddingTop: "env(safe-area-inset-top)",
          paddingBottom: "env(safe-area-inset-bottom)",
          // ✅ تحسين scrollbar
          scrollbarWidth: "thin",
          scrollbarColor: mode === "dark" ? "#444 transparent" : "#ccc transparent",
          "&::-webkit-scrollbar": {
            width: isSmallTablet ? "4px" : "6px",
          },
          "&::-webkit-scrollbar-track": {
            bgcolor: "transparent",
          },
          "&::-webkit-scrollbar-thumb": {
            bgcolor: mode === "dark" ? "#444" : "#bbb",
            borderRadius: "3px",
            "&:hover": {
              bgcolor: mode === "dark" ? "#555" : "#999",
            },
          },
          // ✅ تحسين الأداء
          WebkitOverflowScrolling: "touch",
          willChange: "width",
          // ✅ منع التحديد غير المرغوب
          userSelect: "none",
          WebkitTapHighlightColor: "transparent",
        },
      }}
    >
      {/* ✅ Header محسّن مع معلومات المستخدم */}
      <Toolbar
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          width: "100%",
          p: isSmallTablet ? 1 : 1.5,
          gap: 1,
          bgcolor: mode === "dark" ? "rgba(30, 30, 30, 0.8)" : "rgba(255, 255, 255, 0.8)",
          backdropFilter: "blur(8px)",
          borderBottom: `1px solid ${mode === "dark" ? "#333" : "#eee"}`,
          minHeight: isSmallTablet ? 64 : 80,
        }}
      >
        {/* ✅ زر الطي/الفتح */}
        <Box sx={{ width: "100%", display: "flex", justifyContent: "center" }}>
          <Tooltip title={isOpen ? "إغلاق القائمة" : "فتح القائمة"} placement="left">
            <IconButton
              onClick={toggleSidebar}
              sx={{
                color: "#EA7C69",
                bgcolor: mode === "dark" ? "rgba(234, 124, 105, 0.1)" : "rgba(234, 124, 105, 0.05)",
                width: isSmallTablet ? 36 : 40,
                height: isSmallTablet ? 36 : 40,
                "&:hover": {
                  bgcolor: mode === "dark" ? "rgba(234, 124, 105, 0.2)" : "rgba(234, 124, 105, 0.1)",
                  transform: "scale(1.05)",
                },
                transition: "all 0.2s",
              }}
            >
              {isOpen ? <ChevronRight fontSize={isSmallTablet ? "small" : "medium"} /> : <Menu fontSize={isSmallTablet ? "small" : "medium"} />}
            </IconButton>
          </Tooltip>
        </Box>

      </Toolbar>

      <Divider sx={{ bgcolor: mode === "dark" ? "#333" : "#eee" }} />

      {/* ✅ عرض تاريخ اليومية الحالية */}
      {isOpen && sessionDate && (
        <Box
          sx={{
            width: "100%",
            textAlign: "center",
            py: isSmallTablet ? 1 : 1.5,
            px: 2,
            backgroundColor: mode === "dark" ? "#2a2838" : "#f5f5f5",
            borderBottom: `1px solid ${mode === "dark" ? "#333" : "#EA7C69"}`,
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: mode === "dark" ? "#fff" : "#000",
              fontWeight: 600,
              fontSize: isSmallTablet ? "12px" : "13px",
              whiteSpace: "nowrap",
            }}
          >
            📅 تاريخ اليومية: {sessionDate}
          </Typography>
        </Box>
      )}

      {/* ✅ عرض رسالة في حالة عدم وجود يومية نشطة */}
      {isOpen && !sessionDate && (
        <Box
          sx={{
            width: "100%",
            textAlign: "center",
            py: isSmallTablet ? 0.8 : 1,
            px: 2,
            backgroundColor: mode === "dark" ? "#2a2838" : "#fff3cd",
            borderBottom: `1px solid ${mode === "dark" ? "#333" : "#EA7C69"}`,
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: mode === "dark" ? "#ffc107" : "#856404",
              fontWeight: 500,
              fontSize: isSmallTablet ? "11px" : "12px",
            }}
          >
            ⚠️ لا توجد يومية نشطة
          </Typography>
        </Box>
      )}

      {/* ✅ القائمة محسّنة بالكامل */}
      <List
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          p: isSmallTablet ? 0.5 : 1,
          gap: isSmallTablet ? 0.3 : 0.5,
        }}
      >
        {menuItems
          .filter((item) => allowedPages.includes(item.path))
          .map((item, index) => {
            const isActive = location.pathname === item.path;
            return (
              <Tooltip key={index} title={!isOpen ? item.text : ""} placement="left">
                <ListItem
                  button
                  component={Link}
                  to={item.path}
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: isOpen ? "flex-start" : "center",
                    width: "100%",
                    px: isOpen ? (isSmallTablet ? 1 : 1.5) : (isSmallTablet ? 0.5 : 1),
                    py: isSmallTablet ? 0.8 : 1.2,
                    borderRadius: isSmallTablet ? "10px" : "12px",
                    transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
                    backgroundColor: isActive
                      ? mode === "dark"
                        ? "rgba(234, 124, 105, 0.15)"
                        : "rgba(234, 124, 105, 0.08)"
                      : "transparent",
                    borderRight: isActive ? `${isSmallTablet ? 2 : 3}px solid #EA7C69` : `${isSmallTablet ? 2 : 3}px solid transparent`,
                    // ✅ تحسين touch feedback
                    WebkitTapHighlightColor: "transparent",
                    touchAction: "manipulation",
                    "&:hover": {
                      backgroundColor: isActive
                        ? mode === "dark"
                          ? "rgba(234, 124, 105, 0.25)"
                          : "rgba(234, 124, 105, 0.12)"
                        : mode === "dark"
                        ? "rgba(234, 124, 105, 0.08)"
                        : "rgba(234, 124, 105, 0.05)",
                      transform: "translateX(-4px)",
                    },
                    "&:active": {
                      transform: "translateX(-2px) scale(0.98)",
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      color: isActive ? "#EA7C69" : mode === "dark" ? "#bbb" : "#666",
                      minWidth: isOpen ? (isSmallTablet ? "40px" : "48px") : (isSmallTablet ? "36px" : "40px"),
                      justifyContent: "center",
                      transition: "color 0.2s",
                      "& svg": {
                        fontSize: getIconSize(),
                      },
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  {isOpen && (
                    <ListItemText
                      primary={item.text}
                      sx={{
                        color: isActive ? "#EA7C69" : mode === "dark" ? "#bbb" : "#333",
                        textAlign: "right",
                        "& .MuiTypography-root": {
                          fontSize: getTextSize(),
                          fontWeight: isActive ? 600 : 500,
                          transition: "color 0.2s",
                          lineHeight: 1.4,
                        },
                      }}
                    />
                  )}
                </ListItem>
              </Tooltip>
            );
          })}
      </List>

      {/* ✅ Footer مع معلومات إضافية (اختياري) */}
      {isOpen && (
        <Box
          sx={{
            mt: "auto",
            p: isSmallTablet ? 1 : 1.5,
            borderTop: `1px solid ${mode === "dark" ? "#333" : "#eee"}`,
            textAlign: "center",
          }}
        >
          <Typography
            variant="caption"
            sx={{
              fontSize: isSmallTablet ? "10px" : "11px",
              color: mode === "dark" ? "#666" : "#999",
            }}
          >
            النسخة 2.0
          </Typography>
        </Box>
      )}
    </Drawer>
  );
};

export default Sidebar;
